import React, { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import DynamicLink, { DynamicLinkProps } from '../../../../components/DynamicLink';
import {
  HEIGHT_MOBILE,
  BREAKPOINT_MOBILE,
  BREAKPOINT_PX_DESKTOP,
  BREAKPOINT_DESKTOP,
  BREAKPOINT_PX_LARGE_DESKTOP,
  BREAKPOINT_LARGE_DESKTOP,
} from '../../constants';
import { CallToAction } from 'packages/smart-home-support/styling/styles';
import { NavItem } from 'packages/smart-home-support/assets/models';

const menuFooterHeight = 125;
const breakpointNarrowDesktop = `
  (min-width: ${BREAKPOINT_PX_DESKTOP}px) and (max-width: ${BREAKPOINT_PX_DESKTOP + 100}px),
  (min-width: ${BREAKPOINT_PX_LARGE_DESKTOP}px) and (max-width: ${BREAKPOINT_PX_LARGE_DESKTOP + 100}px)
`;

type MenuSectionProps = {
  items: Array<NavItem>;
  isOpen: boolean;
  onClose: Function;
  className?: string;
  path: string;
};

export const Spacer = styled.div`
  @media ${BREAKPOINT_DESKTOP} {
    width: 30px;
  }
`;

export const MenuFooter = styled.div<{ isMenuOpen: boolean }>`
  position: fixed;
  display: flex;
  align-items: center;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${menuFooterHeight}px
  text-align: center;
  padding: 0 30px;
  transform: translateY(${(props) => (props.isMenuOpen ? 0 : 100)}%);
  background: ${(props) => props.theme.colors.primaryAccentFontColor};
  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    transition: opacity 0.4s;
    opacity: ${(props) => (props.isMenuOpen ? 1 : 0)};
    background: ${(props) =>
      `linear-gradient(180deg, ${props.theme.colors.gradient1Light} -24.61%, ${
        props.theme.colors.gradient1Dark
      } 100%)}`};
    z-index: -1;
  }
  @media ${BREAKPOINT_DESKTOP} {
    transform: none;
    display: flex;
    background: none;
    position: static;
    height: auto;
    padding: 0;
    opacity: 1;
    align-items: center;
    margin-left: 20px;
    &:after {
      display: none;
    }
  }
`;

export const MenuFooterButton = styled(Link)`
  ${CallToAction}
  color: ${(props) => props.theme.colors.primaryAccentFontColor};
  background-color: ${(props) => props.theme.colors.primaryAccent};
  text-transform: ${(props) => props.theme.styles.callToActionTextTransform};
  width: 100%;
  border-radius: 100px;
  font-size: 16px;
  padding: 18px 25px;
  box-sizing: border-box;
  font-weight: 600;
  z-index: 5;
  :hover {
    text-decoration: none;
  }
  @media ${BREAKPOINT_MOBILE} {
    box-shadow: none;
    background: none;
    border: 2px solid ${(props) => props.theme.colors.primaryAccentFontColor};
  }
  @media ${BREAKPOINT_DESKTOP} {
    border: none;
  }
`;

export const MenuContainer = styled.div<{ isMenuOpen: boolean }>`
  position: fixed;
  top: ${HEIGHT_MOBILE}px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  background: #fff;
  opacity: ${(props) => (props.isMenuOpen ? 1 : 0)};
  height: ${(props) => (props.isMenuOpen ? 'auto' : 0)};
  @media ${BREAKPOINT_DESKTOP} {
    height: auto;
    opacity: 1;
    position: static;
    align-items: center;
    display: flex;
    box-shadow: none;
    z-index: 1;
    justify-content: flex-end;
    margin-right: 30px;
  }
  @media ${BREAKPOINT_LARGE_DESKTOP} {
    margin-right: 100px;
  }
`;

export const MenuSections = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: ${menuFooterHeight}px;
  overflow: auto;
  @media ${BREAKPOINT_DESKTOP} {
    position: static;
    flex-direction: row;
    align-self: stretch;
    align-items: stretch;
  }
`;

const MenuItemList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;
  position: relative;
  margin: 0;
  text-align: center;
  padding: 20px 0;
  box-sizing: border-box;
  min-height: 120px;
  &:after {
    content: '';
    border-bottom: 1px solid #b6b6b6;
    position: absolute;
    top: 0;
    left: 53px;
    right: 53px;
  }
  &:first-child {
    &:after {
      display: none;
    }
  }
  @media ${BREAKPOINT_DESKTOP} {
    flex-direction: row;
    align-items: stretch;
    min-height: 0;
    padding: 0;
    &:after {
      display: none;
    }
  }
`;

const menuItemHeight = 60;
const MenuItem = styled.li<{ isMenuOpen: boolean; position: number }>`
  transition: transform 0.2s ease-out ${(props) => props.position * 0.03}s,
    opacity 0.5s ease-out ${(props) => props.position * 0.03}s;
  transform: translateY(${(props) => (props.isMenuOpen ? 0 : 10)}px);
  opacity: ${(props) => (props.isMenuOpen ? 1 : 0)};
  min-height: 20px;
  max-height: ${menuItemHeight}px;
  display: flex;
  align-items: center;
  flex-grow: 1;
  flex-basis: ${menuItemHeight}px;
  @media ${BREAKPOINT_DESKTOP} {
    flex-grow: auto;
    flex-basis: auto;
    align-items: stretch;
    max-height: none;
    margin: 0 10px;
    transform: translateY(0);
    opacity: 1;
    &:first-child {
      margin-left: 0;
    }
  }
  @media ${breakpointNarrowDesktop} {
    margin: 0;
  }
`;

// Added !important to color and text-decoration to override bootstrap classes on experiences page
const MenuItemLink = styled(DynamicLink)<DynamicLinkProps & { active: boolean; label: string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: center;
  height: 100%;
  box-sizing: border-box;
  text-decoration: none;
  color: ${(props) => (props.active ? props.theme.colors.primaryAccent : '#2e2e2e')} !important;
  font-weight: ${(props) => (props.active ? 'bold' : 'inherit')};
  align-items: center;
  justify-content: center;
  @media ${BREAKPOINT_DESKTOP} {
    height: auto;
    font-size: 14px;
    ${globals.REACT_APP_PARTNER === 'SHS-WEB' &&
      `
      font-size: 13px;
      letter-spacing: -.3px;
    `};
    text-decoration: none !important;
    align-self: stretch;
    width: auto;
    justify-content: center;
    padding: 0 15px;
    transition: background 0.2s;
    :before {
      content "${(props) => props.label}";
      font-weight: bold;
      display: block;
      height: 0px;
      overflow: hidden;
      color: transparent;
    }
    :after {
      transition: width, 0.2s;
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 5px;
      width: 0;
      margin: 0 auto;
      background: ${(props) => props.theme.colors.primaryAccent};
    }
    :hover {
      :after {
        width: ${(props) => (props.to === '' ? '0%' : '100%')};
        text-decoration: none !important;
      }
    }
    @media ${breakpointNarrowDesktop} {
      padding: 0 14px;
    }
  }
`;

const getOnClickForMenuItem = (item: NavItem, onClose: Function) => (event: React.MouseEvent) => {
  onClose();
  if (item.onClick) {
    item.onClick(event);
  }
};

const isItemActive = (path: string, item: NavItem): boolean => !!item.to && path.includes(item.to);

const MenuSection: React.SFC<MenuSectionProps> = (props) => (
  <MenuItemList className={props.className}>
    {props.items.map((item, i) => (
      <MenuItem key={i} position={i} isMenuOpen={props.isOpen}>
        <MenuItemLink
          {...item}
          label={item.label}
          onClick={getOnClickForMenuItem(item, props.onClose)}
          active={isItemActive(props.path, item)}
        >
          {item.label}
        </MenuItemLink>
      </MenuItem>
    ))}
  </MenuItemList>
);

export const MenuPrimarySection = styled(MenuSection)`
  padding-top: 40px;
  transform: ${(props) => (props.isOpen ? 'translateY(0)' : 'translateY(100px)')};
  transition: transform 0.5s;
  @media ${BREAKPOINT_DESKTOP} {
    padding-top: 0;
    transition: none;
    transform: translateY(0);
  }
`;



// WEBPACK FOOTER //
// ./packages/smart-home-support/components/Header/components/HeaderMenu/components.tsx